<template>
  <wz-modal :value="value" title="Cesar contrato" @input="$emit('input', $event)">
    <div v-if="contract">
      <p class="mb-3">
        Vas a cesar el contrato <strong>{{ contract.number }}</strong> de
        <strong>{{ contract.fullName }}</strong>. Esta acción es irreversible.
      </p>

      <div class="mb-3">
        <label class="font-medium">Fecha de cese <span class="text-danger">*</span></label>
        <wz-datepicker v-model="ceaseForm.terminationDate" :max="todayIso()" />
      </div>

      <div class="mb-3">
        <label class="font-medium">Tipo de cese <span class="text-danger">*</span></label>
        <wz-select
          v-model="ceaseForm.terminationType"
          :items="reasonOptions"
          defaultOption="Seleccionar tipo"
          @change="onCeaseTypeChange"
        />
      </div>

      <div class="mb-3">
        <label class="font-medium">Motivo de cese <span class="text-danger">*</span></label>
        <wz-select
          v-model="ceaseForm.cessationReasonId"
          :items="cessationReasonOptions"
          :disabled="!ceaseForm.terminationType"
          :defaultOption="ceaseForm.terminationType ? 'Seleccionar motivo (Tabla 17 T-Registro)' : 'Seleccione un tipo primero'"
        />
      </div>

      <div class="mb-3">
        <label class="font-medium">Observaciones</label>
        <wz-textarea v-model="ceaseForm.terminationNotes" rows="5" maxlength="1000" />
        <div class="text-right text-sm" :style="{ color: ceaseNotesCounterColor }">
          {{ (ceaseForm.terminationNotes || '').length }} / 1000
        </div>
      </div>

      <div class="mb-3">
        <label class="font-medium">Documentos de cese
          <small class="text-muted">(PDF, JPG, PNG — máx. 4 archivos, 10MB c/u)</small>
        </label>
        <wz-file-input
          :key="fileKey"
          id-file="cease-docs-upload-shared"
          :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
          @onChange="onDocsSelected"
        />
        <ul v-if="ceaseForm.documents.length" class="mt-2" style="list-style:none; padding:0; margin:0;">
          <li
            v-for="(doc, idx) in ceaseForm.documents"
            :key="idx"
            class="flex items-center justify-between text-sm py-1"
          >
            <span><i class="ri-file-line mr-1"></i>{{ doc.name }}</span>
            <button type="button" class="border-0 bg-transparent text-danger" @click="removeDoc(idx)">
              <i class="ri-close-line"></i>
            </button>
          </li>
        </ul>
      </div>
    </div>

    <template #footer>
      <wz-button variant="outline-secondary" @click="$emit('input', false)">Cancelar</wz-button>
      <wz-button
        variant="danger"
        :disabled="!ceaseForm.terminationDate || !ceaseForm.terminationType || !ceaseForm.cessationReasonId"
        @click="submit()"
      >Cesar</wz-button>
    </template>
  </wz-modal>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { ContractsService, CessationReasonService } from '@/core/services'

export default {
  name: 'CeaseContractModal',
  props: {
    value:    { type: Boolean, default: false },
    // { id, number, fullName }
    contract: { type: Object, default: null },
  },
  emits: ['input', 'ceased'],
  data() {
    return {
      reasonOptions: [],
      cessationReasonOptions: [],
      fileKey: 0,
      ceaseForm: {
        terminationDate: null,
        terminationType: null,
        cessationReasonId: null,
        terminationNotes: '',
        documents: [],
      },
    }
  },
  computed: {
    ceaseNotesCounterColor() {
      const len = (this.ceaseForm.terminationNotes || '').length
      if (len >= 1000) return '#dc3545'
      if (len >= 900) return '#e8a33d'
      return '#6c757d'
    },
  },
  watch: {
    value(open) {
      if (open) this.resetForm()
    },
  },
  methods: {
    todayIso() {
      const d = new Date()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${m}-${day}`
    },
    resetForm() {
      this.ceaseForm = {
        terminationDate: this.todayIso(),
        terminationType: null,
        cessationReasonId: null,
        terminationNotes: '',
        documents: [],
      }
      this.cessationReasonOptions = []
      this.fileKey += 1
    },
    async loadCessationTypes() {
      try {
        this.reasonOptions = (await CessationReasonService.listTypes()).payload || []
      } catch (e) {
        console.error(e)
      }
    },
    async onCeaseTypeChange(type) {
      this.ceaseForm.terminationType = type
      this.ceaseForm.cessationReasonId = null
      this.cessationReasonOptions = []
      if (!type) return
      try {
        const resp = await CessationReasonService.listByType(type)
        this.cessationReasonOptions = (resp.payload || []).map(r => ({ key: r.id, label: r.name }))
      } catch (e) {
        console.error(e)
      }
    },
    onDocsSelected(files) {
      if (!files) return
      Array.from(files).forEach(f => {
        if (this.ceaseForm.documents.length >= 4) {
          Alert.error('Máximo 4 documentos de cese')
          return
        }
        if (f.size > 10 * 1024 * 1024) {
          Alert.error(`${f.name} supera 10MB`)
          return
        }
        if (!this.ceaseForm.documents.some(d => d.name === f.name && d.size === f.size)) {
          this.ceaseForm.documents.push(f)
        }
      })
    },
    removeDoc(idx) {
      this.ceaseForm.documents.splice(idx, 1)
    },
    async submit(confirmed = false) {
      if (!this.contract) return
      if (!this.ceaseForm.terminationDate || !this.ceaseForm.terminationType) {
        Alert.error('Completa la fecha y el tipo de cese')
        return
      }
      try {
        await this.$store.dispatch('app/loading', true)
        const fd = new FormData()
        fd.append('terminationDate', this.ceaseForm.terminationDate)
        fd.append('terminationType', this.ceaseForm.terminationType)
        if (this.ceaseForm.cessationReasonId) fd.append('cessationReasonId', this.ceaseForm.cessationReasonId)
        if (this.ceaseForm.terminationNotes) fd.append('terminationNotes', this.ceaseForm.terminationNotes)
        // DEF: el cese no se bloquea por documentación incompleta, pero exige confirmación explícita.
        if (confirmed) fd.append('complianceOverrideConfirmed', 'true')
        this.ceaseForm.documents.forEach(f => fd.append('documents', f))
        await ContractsService.cease(this.contract.id, fd)
        this.$emit('input', false)
        this.$emit('ceased')
        Alert.success('Contrato cesado correctamente')
      } catch (ex) {
        console.error(ex)
        // Documentación incompleta: no bloquea, se advierte (exposición ante fiscalización) y se confirma.
        if (!confirmed && ex.errors && ex.errors.requiresComplianceConfirmation) {
          await this.$store.dispatch('app/loading', false)
          if (window.confirm(ex.errors.message)) {
            return this.submit(true)
          }
          return
        }
        Alert.error(ex.errors ? ex.errors.message : 'Error al cesar el contrato')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  created() {
    this.loadCessationTypes()
  },
}
</script>
